import { type TResolution } from '@streaming/doppio-player';
import {
  bind,
  EPlaybackState,
  EPlayerType,
  TDeepPartial,
  type TLogLevel,
} from '@streaming/mouflon-utils';
import type * as RistrettoPlayer from '@streaming/ristretto-player';
import { TMMPEventBus } from '@utils/EventBus';

import { IStreamConfig } from '@customTypes/common';
import { TRistrettoEventType } from '@metrics/IMetrics';
import { AbstractWrapper } from '@players/AbstractWrapper';
import { EPlayersEvents, TPlayersEventsPayload } from '@players/Players.events';

export type TWebRTCAbrPreset = RistrettoPlayer.TWebRTCAbrPreset;
export interface IRistrettoPlayerStreamConfig {
  WebRTCSignallingUrl?: string;
  WebRTCStreamId?: string;
  WebRTCACLToken?: string;
}

type TRistrettoModuleNeededFields = {
  EStreamStatus: typeof RistrettoPlayer.EStreamStatus;
  Player: typeof RistrettoPlayer.Player;
  ECommonEvents: typeof RistrettoPlayer.ECommonEvents;
  EPlayerEvent: typeof RistrettoPlayer.EPlayerEvent;
};

const statsConfig: RistrettoPlayer.IRistrettoPlayerConfig['peerConnectionConfig']['statsCollectionScheme'] =
  {
    framesDecoded: {
      originalPath: 'inbound-rtp:video.framesDecoded',
      modifier: 'raw',
      reducer: 'sum',
      filter: 'number',
    },
    nackCount: {
      originalPath: 'inbound-rtp:video.nackCount',
      modifier: 'raw',
      reducer: 'sum',
      filter: 'number',
    },
    totalFreezesDuration: {
      originalPath: 'inbound-rtp:video.totalFreezesDuration',
      modifier: 'raw',
      reducer: 'sum',
      filter: 'number',
    },
    totalPausesDuration: {
      originalPath: 'inbound-rtp:video.totalPausesDuration',
      modifier: 'raw',
      reducer: 'sum',
      filter: 'number',
    },
    fecPacketsReceived: {
      originalPath: 'inbound-rtp.fecPacketsReceived',
      modifier: 'raw',
      reducer: 'sum',
      filter: 'number',
    },
    fecPacketsDiscarded: {
      originalPath: 'inbound-rtp.fecPacketsDiscarded',
      modifier: 'raw',
      reducer: 'sum',
      filter: 'number',
    },
    framesReceived: {
      originalPath: 'inbound-rtp:video.framesReceived',
      modifier: 'raw',
      reducer: 'sum',
      filter: 'number',
    },
    packetsLost: {
      originalPath: 'inbound-rtp.packetsLost',
      modifier: 'raw',
      reducer: 'sum',
      filter: 'number',
    },
    packetsDiscarded: {
      originalPath: 'inbound-rtp.packetsDiscarded',
      modifier: 'raw',
      reducer: 'sum',
      filter: 'number',
    },
    packetsReceived: {
      originalPath: 'inbound-rtp.packetsReceived',
      modifier: 'raw',
      reducer: 'sum',
      filter: 'number',
    },
    totalRoundTripTime: {
      originalPath: 'candidate-pair.totalRoundTripTime',
      modifier: 'raw',
      reducer: 'sum',
      filter: 'nonzero',
    },
    responsesReceived: {
      originalPath: 'candidate-pair.responsesReceived',
      modifier: 'raw',
      reducer: 'sum',
      filter: 'nonzero',
    },
    audioJitter: {
      originalPath: 'inbound-rtp:audio.jitter',
      modifier: 'raw',
      reducer: 'sum',
      filter: 'number',
    },
    videoJitter: {
      originalPath: 'inbound-rtp:video.jitter',
      modifier: 'raw',
      reducer: 'sum',
      filter: 'number',
    },
  };

const statsSchemeKeys: string[] = Object.keys(statsConfig);

export class RistrettoPlayerWrapper extends AbstractWrapper<
  RistrettoPlayer.IRistrettoPlayerConfig,
  IRistrettoPlayerStreamConfig,
  RistrettoPlayer.Player
> {
  private _ristrettoModuleCache: TRistrettoModuleNeededFields | null = null;
  private _abrResolutions: RistrettoPlayer.TWebRTCAbrPreset[] = [];
  private _isABREnabled: boolean = false;
  private _isLoadingPlayerModule: boolean = false;
  private _logLevels: TLogLevel[] = [];
  private _ristrettoPlaybackStateToMMPStateMap: Record<
    RistrettoPlayer.EStreamStatus,
    EPlaybackState
  > | null = null;
  private _toggleTurnServersListener: string;

  constructor(eventBusInstance: TMMPEventBus, options: { persistent?: boolean } = {}) {
    super(eventBusInstance, options);
    this._toggleTurnServersListener = eventBusInstance.subscribe(
      EPlayersEvents.TOGGLE_TURN_SERVERS,
      this._toggleTURNServers,
    );
  }

  public destroy(): AbstractWrapper<
    RistrettoPlayer.IRistrettoPlayerConfig,
    IRistrettoPlayerStreamConfig,
    RistrettoPlayer.Player
  > {
    this._eventBusInstance.unsubscribe(this._toggleTurnServersListener);
    return super.destroy();
  }

  public setStreamConfig(config: IStreamConfig | undefined): RistrettoPlayerWrapper {
    if (!config?.WebRTCSignallingUrl) {
      this._lastKnownStreamConfig = null;
      return this;
    }

    this._lastKnownStreamConfig = {
      WebRTCStreamId: config.WebRTCStreamId,
      WebRTCACLToken: config?.WebRTCACLToken,
      WebRTCSignallingUrl: config?.WebRTCSignallingUrl,
    };

    this._setStreamConfig();

    return this;
  }

  public enableAbr(): RistrettoPlayerWrapper {
    this._isABREnabled = true;
    this._playerInstance?.enableABR();

    return this;
  }

  public disableAbr(): RistrettoPlayerWrapper {
    this._isABREnabled = false;
    this._playerInstance?.disableABR();

    return this;
  }

  public setAbrResolutions(
    resolutions: RistrettoPlayer.TWebRTCAbrPreset[],
  ): RistrettoPlayerWrapper {
    this._abrResolutions = resolutions;
    this._playerInstance?.setAbrResolutionsVariants(resolutions);

    return this;
  }

  public setLoggerLevel(levels: TLogLevel[]): void {
    this._playerInstance?.setLoggerLevels(levels);
    this._logLevels = levels;
  }

  public setResolutionByName(resolutionName: string): RistrettoPlayerWrapper {
    this._playerInstance?.setQualityByName(resolutionName);

    return this;
  }

  public setResolutionByBitrate(bitrate: number): RistrettoPlayerWrapper {
    this._playerInstance?.setQualityByBitrate(bitrate);

    return this;
  }

  public recreatePlayerInstance(): void {
    if (this._playerInstance && !this._persistent) {
      this.destroy();
      this._playerInstance = null;
    }

    this._createPlayerInstance();
  }

  public setPlayerConfig(
    config: TDeepPartial<RistrettoPlayer.IRistrettoPlayerConfig>,
  ): RistrettoPlayerWrapper {
    this._lastKnownConfig = config;

    if (!this._lastKnownConfig.peerConnectionConfig) {
      this._lastKnownConfig.peerConnectionConfig = {};
    }

    if (!this._lastKnownConfig.peerConnectionConfig?.statsCollectionScheme) {
      this._lastKnownConfig.peerConnectionConfig = { statsCollectionScheme: statsConfig };
    } else {
      this._lastKnownConfig.peerConnectionConfig.statsCollectionScheme = {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ...(this._lastKnownConfig.peerConnectionConfig
          .statsCollectionScheme as RistrettoPlayer.IRistrettoPlayerConfig['peerConnectionConfig']['statsCollectionScheme']),
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ...statsConfig,
      };
    }
    this._playerInstance?.setConfig(config, true);

    return this;
  }

  get version(): string | undefined {
    return this._playerInstance?.version;
  }

  protected _attachEventListeners(): void {
    if (!this._ristrettoModuleCache || !this._playerInstance) {
      return;
    }

    this._playerInstance.addEventListener(
      this._ristrettoModuleCache.ECommonEvents.DEBUGGER_CONNECTED,
      this._onDebuggerConnected,
    );
    this._playerInstance.addEventListener(
      this._ristrettoModuleCache.EPlayerEvent.PLAY_STATE_CHANGED,
      this._onPlaybackStateChanged,
    );
    this._playerInstance.addEventListener(
      this._ristrettoModuleCache.ECommonEvents.ERROR,
      this._onRistrettoError,
    );
    this._playerInstance.addEventListener(
      this._ristrettoModuleCache.ECommonEvents.PEER_METRICS_AVAILABLE,
      this._onPeerMetricsAvailable,
    );
    this._playerInstance.addEventListener(
      this._ristrettoModuleCache.ECommonEvents.LIFECYCLE_EVENT,
      this._onLifecycleEventReported,
    );
    this._playerInstance.addEventListener(
      this._ristrettoModuleCache.ECommonEvents.LIFECYCLE_ERROR,
      this._onLifecycleErrorReported,
    );
    this._playerInstance.addEventListener(
      this._ristrettoModuleCache.ECommonEvents.RESOLUTION_SWITCH_AUTO,
      this._onRenditionSwitchAuto,
    );
    this._playerInstance.addEventListener(
      this._ristrettoModuleCache.ECommonEvents.RESOLUTIONS_AVAILABLE,
      this._onResolutionsAvailable,
    );
    this._playerInstance.addEventListener(
      this._ristrettoModuleCache.ECommonEvents.QUALITY_CHANGED,
      this._onQualityChanged,
    );
  }

  protected _detachEventListeners(): void {
    if (!this._ristrettoModuleCache || !this._playerInstance) {
      return;
    }

    this._playerInstance.removeEventListener(
      this._ristrettoModuleCache.ECommonEvents.DEBUGGER_CONNECTED,
      this._onDebuggerConnected,
    );
    this._playerInstance.removeEventListener(
      this._ristrettoModuleCache.EPlayerEvent.PLAY_STATE_CHANGED,
      this._onPlaybackStateChanged,
    );
    this._playerInstance.removeEventListener(
      this._ristrettoModuleCache.ECommonEvents.ERROR,
      this._onRistrettoError,
    );
    this._playerInstance.removeEventListener(
      this._ristrettoModuleCache.ECommonEvents.PEER_METRICS_AVAILABLE,
      this._onPeerMetricsAvailable,
    );
    this._playerInstance.removeEventListener(
      this._ristrettoModuleCache.ECommonEvents.RESOLUTION_SWITCH_AUTO,
      this._onRenditionSwitchAuto,
    );
    this._playerInstance.removeEventListener(
      this._ristrettoModuleCache.ECommonEvents.RESOLUTIONS_AVAILABLE,
      this._onResolutionsAvailable,
    );
    this._playerInstance.removeEventListener(
      this._ristrettoModuleCache.ECommonEvents.QUALITY_CHANGED,
      this._onQualityChanged,
    );
  }

  protected _createPlayerInstance(): void {
    if (!this._ristrettoModuleCache) {
      this._loadRistrettoModule();
      return;
    }

    if (!this._persistent || !this._playerInstance) {
      this._destroyPlayerInstance();
      this._playerInstance = new this._ristrettoModuleCache.Player();
    }

    if (this._lastKnownConfig) {
      this._playerInstance.setConfig(this._lastKnownConfig, true);
    }

    this._playerInstance?.setConfig({
      peerConnectionConfig: { statsCollectionScheme: statsConfig, statsGatheringPeriod: 500 },
    });

    this._setStreamConfig();
    this._playerInstance.setAppKey(this._appKey);

    if (this._videoElement) {
      this._playerInstance.setVideoElement(this._videoElement);
    }

    this._attachEventListeners();

    this._playerInstance?.setAppKey('callbackApp');

    if (this._isStarted) {
      this._playerInstance?.start();
    }

    if (this._isABREnabled) {
      this._playerInstance?.enableABR();
    } else {
      this._playerInstance?.disableABR();
    }

    this._playerInstance?.setAbrResolutionsVariants(this._abrResolutions);
    this._playerInstance?.setLoggerLevels(this._logLevels);
  }

  private _setStreamConfig(): void {
    if (this._playerInstance && this._lastKnownStreamConfig) {
      this._playerInstance?.setStreamConfig(
        {
          aclToken: this._lastKnownStreamConfig.WebRTCACLToken,
          streamId: this._lastKnownStreamConfig.WebRTCStreamId,
          wsUrl: this._lastKnownStreamConfig.WebRTCSignallingUrl,
        },
        true,
      );
    }
  }

  private async _loadRistrettoModule(): Promise<void> {
    if (this._isLoadingPlayerModule) {
      return;
    }

    this._isLoadingPlayerModule = true;
    const { EStreamStatus, Player, ECommonEvents, EPlayerEvent } = await import(
      '@streaming/ristretto-player'
    );

    this._ristrettoModuleCache = {
      EStreamStatus,
      Player,
      ECommonEvents,
      EPlayerEvent,
    };

    this._ristrettoPlaybackStateToMMPStateMap = {
      [EStreamStatus.UNKNOWN]: EPlaybackState.IDLE,
      [EStreamStatus.CONNECTING]: EPlaybackState.LOADING,
      [EStreamStatus.LIVE]: EPlaybackState.PLAYING,
      [EStreamStatus.FINISHED]: EPlaybackState.PAUSED,
      [EStreamStatus.ERRORED]: EPlaybackState.ERROR,
    };

    this._isLoadingPlayerModule = false;
    this._createPlayerInstance();
  }

  @bind
  private _onDebuggerConnected({
    detail: { debuggerUrl },
  }: CustomEvent<{ debuggerUrl: string }>): void {
    this._eventBusInstance.publish(EPlayersEvents.DEBUGGER_CONNECTED, { debuggerUrl: debuggerUrl });
  }

  @bind
  private _onPlaybackStateChanged({
    detail: { prevState, nextState },
  }: CustomEvent<{
    prevState: RistrettoPlayer.EStreamStatus;
    nextState: RistrettoPlayer.EStreamStatus;
  }>): void {
    if (!this._ristrettoPlaybackStateToMMPStateMap) {
      return;
    }

    this._eventBusInstance.publish(EPlayersEvents.PLAYBACK_STATE_CHANGED, {
      nextState: this._ristrettoPlaybackStateToMMPStateMap[nextState],
      prevState: this._ristrettoPlaybackStateToMMPStateMap[prevState],
    });
  }

  @bind
  private _onRistrettoError({ detail }: CustomEvent<RistrettoPlayer.RistrettoPlayerError>): void {
    this._eventBusInstance.publish(EPlayersEvents.ERROR, {
      error: new Error(`Ristretto player error: ${detail.info}`),
      isFatal: detail.isFatal,
      playerType: EPlayerType.WEBRTC,
    });
  }

  @bind
  private _onPeerMetricsAvailable({
    detail: { metrics },
  }: CustomEvent<{ metrics: Record<string, number> }>): void {
    const validMetrics: Record<string, number> = statsSchemeKeys.reduce((s, key) => {
      if (Object.hasOwn(metrics, key)) {
        s[key] = metrics[key];
      }

      return s;
    }, {} as Record<string, number>);
    this._metricsCollector?.registerMetrics(validMetrics);
  }

  @bind
  private _onLifecycleEventReported({
    detail: { eventType },
  }: CustomEvent<{ eventType: string }>): void {
    this._metricsCollector?.registerEvent(eventType as TRistrettoEventType);
  }

  @bind
  private _onLifecycleErrorReported({
    detail: { errorType, nativeError },
  }: CustomEvent<{ errorType: string; nativeError?: Error }>): void {
    this._metricsCollector?.registerEvent(errorType as TRistrettoEventType, {
      errorMessage: nativeError?.message || '',
    });
  }

  @bind
  private _onResolutionsAvailable({
    detail: { resolutions },
  }: CustomEvent<{ resolutions: TResolution[] }>): void {
    this._eventBusInstance.publish(EPlayersEvents.RESOLUTIONS_AVAILABLE, { resolutions });
  }

  @bind
  private _onRenditionSwitchAuto({
    detail: { prevResolution, nextResolution },
  }: CustomEvent<{ prevResolution: string; nextResolution: string }>): void {
    this._metricsCollector?.registerEvent('RenditionSwitchedAuto', {
      prevResolution,
      nextResolution,
    });

    this._eventBusInstance.publish(EPlayersEvents.AUTO_RESOLUTION_SWITCHED, {
      name: nextResolution,
    });
  }

  @bind
  private _toggleTURNServers({
    shouldUseTURNServers,
  }: TPlayersEventsPayload<EPlayersEvents.TOGGLE_TURN_SERVERS>): void {
    this._playerInstance?.toggleTURNServersUsage(shouldUseTURNServers);
  }

  @bind
  private _onQualityChanged({ detail: { name } }: CustomEvent<{ name: string }>): void {
    this._eventBusInstance.publish(EPlayersEvents.RESOLUTION_SWITCHED, { name });
  }
}
